import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { api, auth, API } from "@/lib/api";
import { useBranding } from "@/lib/branding";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle2, PlayCircle, FileText, ExternalLink, Loader2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

const PING_EVERY = 5; // seconds

function ytEmbed(url) {
  if (!url) return null;
  try {
    const u = new URL(url);
    if (u.hostname.includes("youtu.be")) {
      return `https://www.youtube.com/embed/${u.pathname.slice(1)}?autoplay=0`;
    }
    if (u.hostname.includes("youtube.com")) {
      const v = u.searchParams.get("v");
      if (v) return `https://www.youtube.com/embed/${v}?autoplay=0`;
    }
    if (u.hostname.includes("vimeo.com")) {
      const id = u.pathname.split("/").filter(Boolean).pop();
      if (id) return `https://player.vimeo.com/video/${id}`;
    }
  } catch { /* ignore */ }
  return null;
}

export default function TrainingViewer() {
  const { token } = useParams();
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const { branding } = useBranding();
  const brand = branding?.theme_color || "#0EA5E9";

  // Identity comes from three fallbacks (highest priority first):
  //   1. Active login session (logged-in employee — no re-verification needed)
  //   2. URL query string ?emp_id=&email=&dob=
  //   3. sessionStorage `flow-creds-{token}` (persisted when opening from QuizEntry)
  const sessionUser = auth.user();
  const isLoggedInEmp = !!(auth.token() && sessionUser?.emp_db_id);
  const creds = (() => {
    if (isLoggedInEmp) {
      return { emp_id: sessionUser?.emp_id || "", email: sessionUser?.email || "", dob: "" };
    }
    const fromUrl = { emp_id: sp.get("emp_id") || "", email: sp.get("email") || "", dob: sp.get("dob") || "" };
    if (fromUrl.emp_id) return fromUrl;
    try { return JSON.parse(sessionStorage.getItem(`flow-creds-${token}`) || "null") || fromUrl; } catch { return fromUrl; }
  })();

  const [start, setStart] = useState(null); // { progress_id, asset, min_duration_sec, total_open_seconds }
  const [elapsed, setElapsed] = useState(0); // seconds opened in this session + previous
  const [loading, setLoading] = useState(true);
  const [completing, setCompleting] = useState(false);
  const tickRef = useRef(0);

  useEffect(() => {
    // Only block on missing identity when NOT logged in — the backend will resolve emp
    // from the session token when it's a logged-in employee call.
    if (!isLoggedInEmp && !creds?.emp_id) { toast.error("Missing employee identity"); nav(`/q/${token}`); return; }
    api.post("/public/q/training/start", { token, ...creds }).then((r) => {
      setStart(r.data);
      setElapsed(r.data.total_open_seconds || 0);
    }).catch((e) => {
      toast.error(e?.response?.data?.detail || "Couldn't open training");
      nav(`/q/${token}`);
    }).finally(() => setLoading(false));
  }, [token]);

  // Tick + periodic ping
  useEffect(() => {
    if (!start?.progress_id) return;
    if (start?.status === "completed") return;
    const tick = setInterval(() => {
      tickRef.current += 1;
      setElapsed((e) => e + 1);
      if (tickRef.current >= PING_EVERY) {
        const delta = tickRef.current;
        tickRef.current = 0;
        api.post("/public/q/training/ping", { progress_id: start.progress_id, seconds_delta: delta }).catch(() => {});
      }
    }, 1000);
    return () => clearInterval(tick);
  }, [start?.progress_id, start?.status]);

  // Block copy / cut / context menu / print at document level while viewer is open
  useEffect(() => {
    const stop = (e) => { e.preventDefault(); return false; };
    const stopKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && ["c", "x", "s", "p", "u", "a"].includes((e.key || "").toLowerCase())) {
        e.preventDefault();
        toast.info("This action is disabled to protect training material.");
      }
      if (e.key === "PrintScreen") {
        toast.info("Screenshots are logged for training-material protection.");
      }
    };
    document.addEventListener("contextmenu", stop);
    document.addEventListener("copy", stop);
    document.addEventListener("cut", stop);
    document.addEventListener("selectstart", stop);
    document.addEventListener("dragstart", stop);
    document.addEventListener("keydown", stopKey);
    return () => {
      document.removeEventListener("contextmenu", stop);
      document.removeEventListener("copy", stop);
      document.removeEventListener("cut", stop);
      document.removeEventListener("selectstart", stop);
      document.removeEventListener("dragstart", stop);
      document.removeEventListener("keydown", stopKey);
    };
  }, []);

  async function markComplete() {
    if (!start?.progress_id) return;
    // flush pending ticks first
    if (tickRef.current > 0) {
      try { await api.post("/public/q/training/ping", { progress_id: start.progress_id, seconds_delta: tickRef.current }); } catch { /* ignore */ }
      tickRef.current = 0;
    }
    setCompleting(true);
    try {
      await api.post("/public/q/training/complete", { progress_id: start.progress_id });
      toast.success("Training marked complete!");
      nav(`/q/${token}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't complete");
    } finally { setCompleting(false); }
  }

  if (loading) {
    return <div className="min-h-screen grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-sky-500"/></div>;
  }
  if (!start) return null;

  const minSec = start.min_duration_sec || 60;
  const remaining = Math.max(0, minSec - elapsed);
  const pct = Math.min(100, Math.round((elapsed / minSec) * 100));
  const canComplete = elapsed >= minSec;
  const asset = start.asset || {};
  const yt = asset.type === "video" ? ytEmbed(asset.url) : null;
  const fileUrlPub = asset.storage_path ? `${API}/public/training-asset/${asset.storage_path}` : "";

  const empName = creds?.emp_id || "Learner";
  const watermarkText = `${empName} · ${new Date().toLocaleString()} · ${branding?.hospital_name || "MedLearn"}`;

  return (
    <div className="min-h-screen select-none" style={{ background: `linear-gradient(180deg, ${brand}10, #f8fafc)` }} data-testid="training-viewer-secure">
      <style>{`
        @media print { body { display: none !important; } }
        .tv-watermark {
          position: absolute; inset: 0; pointer-events: none;
          background-image: repeating-linear-gradient(-30deg, transparent 0 120px, rgba(15,23,42,0.05) 120px 240px);
          overflow: hidden;
        }
        .tv-watermark::after {
          content: attr(data-wm);
          position: absolute; top: 50%; left: 50%;
          transform: translate(-50%, -50%) rotate(-25deg);
          font-size: 22px; font-weight: 900; letter-spacing: 2px;
          color: rgba(15,23,42,0.13); white-space: nowrap; pointer-events: none;
        }
      `}</style>
      <div className="max-w-3xl mx-auto p-5 lg:p-8 space-y-5">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => nav(`/q/${token}`)} data-testid="training-back">
            <ArrowLeft className="h-4 w-4 mr-1"/> Back to assessment
          </Button>
        </div>

        <header>
          <div className="text-xs uppercase tracking-[0.22em] font-bold" style={{ color: brand }}>Step 2 · Training</div>
          <h1 className="font-display font-black text-3xl text-slate-900 mt-1">{asset.name || "Mandatory Training"}</h1>
          <p className="text-slate-500 mt-1">Engage with the content for at least {minSec} seconds. The Post-Test unlocks once you mark it complete.</p>
        </header>

        <Card className="border-slate-200 rounded-2xl overflow-hidden">
          <CardContent className="p-0">
            <div className="relative" data-testid="secure-content">
              <div className="tv-watermark" data-wm={watermarkText}/>
              {asset.type === "video" && yt && (
                <div className="aspect-video bg-black relative">
                  <iframe className="w-full h-full" src={yt} title="Training video" allow="autoplay; encrypted-media; picture-in-picture" allowFullScreen data-testid="training-video"/>
                  <div className="tv-watermark" data-wm={watermarkText}/>
                </div>
              )}
              {asset.type === "video" && !yt && asset.url && (
                <video controls className="w-full" controlsList="nodownload noremoteplayback" disablePictureInPicture data-testid="training-video"><source src={asset.url}/></video>
              )}
              {asset.type === "link" && (
                <div className="p-8 text-center space-y-4">
                  <div className="h-14 w-14 rounded-full grid place-items-center mx-auto" style={{ background: brand + "22" }}>
                    <ExternalLink className="h-7 w-7" style={{ color: brand }}/>
                  </div>
                  <div className="font-bold text-lg">Open the training material</div>
                  <Button asChild className="rounded-xl" style={{ background: brand, color: "white" }} data-testid="training-link-btn">
                    <a href={asset.url} target="_blank" rel="noreferrer noopener">Open in secure tab</a>
                  </Button>
                  <p className="text-xs text-slate-500">Keep this tab open — your timer keeps ticking here.</p>
                </div>
              )}
              {(asset.type === "pdf" || asset.type === "ppt") && (asset.storage_path || asset.url) && (
                <div className="bg-slate-100 relative">
                  {asset.type === "pdf" ? (
                    <iframe className="w-full h-[70vh]" src={(asset.storage_path ? fileUrlPub : asset.url) + "#toolbar=0&navpanes=0&scrollbar=0"} title="Training PDF" data-testid="training-pdf"/>
                  ) : (
                    <iframe className="w-full h-[70vh]" src={`https://docs.google.com/viewer?embedded=true&url=${encodeURIComponent(asset.storage_path ? fileUrlPub : asset.url)}`} title="Training PPT" data-testid="training-ppt"/>
                  )}
                  <div className="p-3 text-xs text-slate-500 flex items-center gap-2 border-t border-slate-200">
                    <FileText className="h-4 w-4"/>{asset.name || "Training document"}
                    <span className="ml-auto text-[10px] uppercase tracking-wider text-amber-600 font-bold" data-testid="protected-notice">🔒 Protected · Downloading disabled</span>
                  </div>
                </div>
              )}
              {!asset.type && (
                <div className="p-10 text-center text-slate-500">No training material configured.</div>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 rounded-2xl">
          <CardContent className="p-5 space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs uppercase tracking-wider text-slate-500 font-bold">Engagement timer</div>
                <div className="text-2xl font-display font-black text-slate-900" data-testid="training-elapsed">{Math.floor(elapsed/60)}:{String(elapsed%60).padStart(2, "0")}</div>
                <div className="text-xs text-slate-500">{remaining > 0 ? `${remaining}s remaining` : "Minimum time reached"}</div>
              </div>
              <Button onClick={markComplete} disabled={!canComplete || completing}
                className="rounded-xl h-12 px-5 text-base font-bold"
                style={{ background: canComplete ? brand : "#cbd5e1", color: "white" }}
                data-testid="training-complete-btn">
                {completing ? <Loader2 className="h-4 w-4 animate-spin"/> : <><CheckCircle2 className="h-4 w-4 mr-2"/>Mark Training Complete</>}
              </Button>
            </div>
            <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
              <div className="h-full transition-all duration-500" style={{ width: `${pct}%`, background: brand }}/>
            </div>
            <div className="text-[11px] text-slate-400 flex items-center gap-1.5"><PlayCircle className="h-3 w-3"/>Heartbeat saves every {PING_EVERY}s — refresh-safe.</div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
